// Package imports:
import React, { useEffect, useMemo, useState } from "react";
// Component imports:
import DisplayBox from "../../ui-elements/DisplayBox/DisplayBox";
import Table, { IColumn } from "../../ui-elements/Table/Table";
import HeadSection from "../../ui-elements/HeadSection/HeadSection";
import Label from "../../ui-elements/Label/Label";
import { formatCategories, IPepArray, IPepData } from "./PepPage";
import Loading from "../../ui-elements/Loading/Loading";
import Alert from "../../ui-elements/Alert/Alert";
import Link from "../../ui-elements/Link/Link";
import { relatesToFormer, sortEntries } from "./DomesticSearch";
import Button from "../../ui-elements/Button/Button";
import ForeignPepModal from "./ForeignPepModal";
// Service imports:
import { getTableDate, insertDash, isSSN } from "../../services/utils";
import { formatFullName } from "../../services/pepWatchUtils";
// Type imports:
import { IKeldanApiResponse } from "../../types/KeldanTypes";
import {
  IJavaDate,
  IPepType,
  ParsedSanctionsResponse,
  PepHistorySnapshot,
  PepResult,
  PepSnapshotEntry,
  PepWatchListData,
  PepWatchResponse,
} from "../../types/PepTypes";
// Static data imports:
import { CountryList } from "../../assets/staticData/CountryList";

// ============================================================================
// TYPES AND INTERFACES
// ============================================================================

interface ParsedPepHistory {
  snapshotEntries: PepSnapshotEntry[];
  snapshotTimestamp: IJavaDate;
}

interface GroupedWatchPeriod {
  typeOfWatch: "PEP" | "SANCTIONS";
  rate: "daily" | "never" | "quarterly";
  timeRange: {
    start: Date;
    end: Date;
    duration: string;
  };
  recordCount: number;
  isCurrent: boolean;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Parses PEP history snapshot response from API into a more usable format
 */
export function parsePepHistoryResponse(apiResponse: PepHistorySnapshot[]): ParsedPepHistory[] {
  if (!apiResponse || apiResponse.length === 0) {
    return [];
  }
  return apiResponse.map((snapshot) => ({
    snapshotEntries: JSON.parse(snapshot.snapshotEntries) as PepSnapshotEntry[],
    snapshotTimestamp: snapshot.snapshotTimestamp,
  }));
}

/**
 * Parses the lastSanctionsResponse JSON strings from PEP watch history data
 */
export const parseSanctionsResponses = (historyData: PepWatchListData[] | null): PepResult[] => {
  if (!historyData || historyData.length === 0) {
    return [];
  }

  const parsedResponses: PepResult[] = [];

  for (const item of historyData) {
    // Skip items without lastSanctionsResponse or with empty response
    if (!item.lastSanctionsResponse || item.lastSanctionsResponse.trim() === "") {
      continue;
    }

    try {
      // Parse the JSON string
      const parsed = JSON.parse(item.lastSanctionsResponse) as ParsedSanctionsResponse;
      if (parsed && parsed.match) {
        if (Array.isArray(parsed.match)) {
          parsedResponses.push(...parsed.match);
        } else {
          parsedResponses.push(parsed.match);
        }
      }
    } catch (error) {
      console.error("Failed to parse lastSanctionsResponse for item:", item.id, error);
      // Continue with other items even if one fails to parse
    }
  }

  return parsedResponses;
};

/**
 * Sorts and formats PEP history snapshot entries for display
 */
export const sortHistorySnapshotEntries = (snapshotEntries: PepSnapshotEntry[] | null): IPepArray[] => {
  if (!snapshotEntries || snapshotEntries.length === 0) {
    return [];
  }

  const arrayToReturn: IPepArray[] = [];

  // Determine primary vs secondary entries based on your logic:
  // Primary = both relates_to_name AND relates_to_ssn are null/empty
  const primaryData = snapshotEntries.filter(
    (x) =>
      (!x.relates_to_name || x.relates_to_name.trim() === "") && (!x.relates_to_ssn || x.relates_to_ssn.trim() === "")
  );

  const secondaryData = snapshotEntries.filter(
    (x) =>
      (x.relates_to_name && x.relates_to_name.trim() !== "") || (x.relates_to_ssn && x.relates_to_ssn.trim() !== "")
  );

  // Process primary entries (direct PEP entries for this person)
  for (const x of primaryData) {
    arrayToReturn.push({
      name: `${x.name}`, // No SSN available in snapshot entries, just the name
      connection: "Frumaðili",
      status: `${x.pos} (${x.category})`,
      endDate: x.end_date, // Add end date for primary entries
      source: relatesToFormer(x.end_date) ? (
        x.notes
      ) : (
        <Link url={x.source ?? "#"} linkSize="14" target="_blank">
          {x.source}
        </Link>
      ),
    });
  }

  // Process secondary entries (relationships to other PEP individuals)
  for (const y of secondaryData) {
    arrayToReturn.push({
      name: `${y.relates_to_name}${y.relates_to_ssn ? ` (${insertDash(y.relates_to_ssn)})` : ""}`,
      connection: `${y.category}`,
      status: `${y.relates_to_pos} (${y.relates_to_category})`,
      endDate: y.relates_to_end_date, // Add end date for secondary entries
      source: relatesToFormer(y.relates_to_end_date) ? (
        y.relates_to_notes
      ) : (
        <Link url={y.relates_to_source ?? "#"} linkSize="14" target="_blank">
          {y.relates_to_source}
        </Link>
      ),
    });
  }

  return arrayToReturn;
};

/**
 * Helper function to translate frequency codes to Icelandic
 */
const translateFrequency = (frequency: string): string => {
  const translations: Record<string, string> = {
    daily: "Daglega",
    never: "Aldrei",
    quarterly: "Ársfjórðungslega",
    weekly: "Vikulega",
    monthly: "Mánaðarlega",
    annually: "Árlega",
  };
  return translations[frequency] || frequency;
};

/**
 * Helper function to translate watch type codes to Icelandic
 */
const translateWatchType = (type: string): string => {
  const translations: Record<string, string> = {
    PEP: "Stjórnmálaleg tengsl (PEP)",
    SANCTIONS: "Alþjóðlegir válistar",
  };
  return translations[type] || type;
};

// ============================================================================
// WATCH HISTORY GROUPER CLASS
// ============================================================================

/**
 * Class responsible for grouping and analyzing watch history data into meaningful periods
 */
class WatchHistoryGrouper {
  /**
   * Converts various timestamp formats to a standardized Date object
   * Handles both timestampUpdated objects with time property and direct timestamps
   */
  private convertTimestamp(timestamp: any): Date {
    if (timestamp && timestamp.time) {
      return new Date(timestamp.time);
    }
    return new Date(timestamp);
  }

  /**
   * Calculates human-readable duration between two dates in Icelandic
   * Returns appropriate time units (days/hours) based on the duration
   */
  private calculateDuration(start: Date, end: Date): string {
    const diffMs = end.getTime() - start.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (diffDays > 0) {
      return `${diffDays} dag${diffDays !== 1 ? "ar" : ""}`;
    } else if (diffHours > 0) {
      return `${diffHours} klukkustund${diffHours !== 1 ? "ir" : ""}`;
    } else {
      return "Innan við klukkustund";
    }
  }

  /**
   * Creates a synthetic current record from current data for processing
   * This allows us to treat current data as the latest record in the timeline
   */
  private createCurrentRecord(currentData: any): PepWatchListData {
    return {
      ...currentData,
      timestampUpdated: currentData.timestampUpdated,
      pepFrequency: currentData.pepFrequency || "never",
      sanctionsFrequency: currentData.sanctionsFrequency || "never"
    } as PepWatchListData;
  }

  /**
   * Groups watch history records into periods based on frequency changes
   * Now includes both historical records and current data state
   * 
   * @param historyRecords - Historical watch records from the database
   * @param currentData - Current state of the watch configuration
   * @returns Array of grouped periods showing frequency changes over time
   */
  public groupWatchHistory(
    historyRecords: PepWatchListData[], 
    currentData?: any
  ): GroupedWatchPeriod[] {
    // Return empty array if no data is available
    if (!historyRecords || historyRecords.length === 0) {
      return [];
    }

    // Create a combined dataset that includes both history and current state
    let allRecords = [...historyRecords];
    
    // If current data exists and is different from the last historical record, add it
    if (currentData && !(currentData instanceof Error)) {
      const lastHistoryRecord = historyRecords[historyRecords.length - 1];
      const shouldAddCurrentData = 
        !lastHistoryRecord ||
        lastHistoryRecord.pepFrequency !== currentData.pepFrequency ||
        lastHistoryRecord.sanctionsFrequency !== currentData.sanctionsFrequency;

      if (shouldAddCurrentData) {
        allRecords.push(this.createCurrentRecord(currentData));
      }
    }

    // Sort all records by timestamp to ensure chronological order
    const sortedRecords = allRecords.sort(
      (a, b) =>
        this.convertTimestamp(a.timestampUpdated).getTime() - 
        this.convertTimestamp(b.timestampUpdated).getTime()
    );

    const groupedPeriods: GroupedWatchPeriod[] = [];

    // Process frequency changes for PEP monitoring
    this.processFrequencyChanges(sortedRecords, "pepFrequency", "PEP", groupedPeriods);
    
    // Process frequency changes for Sanctions monitoring
    this.processFrequencyChanges(sortedRecords, "sanctionsFrequency", "SANCTIONS", groupedPeriods);

    return groupedPeriods;
  }

  /**
   * Processes frequency changes for a specific watch type (PEP or SANCTIONS)
   * Creates grouped periods whenever the monitoring frequency changes
   * 
   * @param records - All records sorted chronologically
   * @param frequencyField - Which frequency field to monitor ('pepFrequency' | 'sanctionsFrequency')
   * @param watchType - Type of monitoring ('PEP' | 'SANCTIONS')
   * @param groupedPeriods - Array to add the grouped periods to
   */
  private processFrequencyChanges(
    records: PepWatchListData[],
    frequencyField: "pepFrequency" | "sanctionsFrequency",
    watchType: "PEP" | "SANCTIONS",
    groupedPeriods: GroupedWatchPeriod[]
  ): void {
    // Initialize tracking variables with the first record
    let currentRate = records[0][frequencyField] as "daily" | "never" | "quarterly";
    let currentGroupStart = this.convertTimestamp(records[0].timestampUpdated);
    let recordCount = 1;

    // Iterate through remaining records to detect frequency changes
    for (let i = 1; i < records.length; i++) {
      const record = records[i];
      const recordRate = record[frequencyField] as "daily" | "never" | "quarterly";

      // Check if the monitoring frequency has changed
      if (recordRate !== currentRate) {
        // Frequency changed - close the current group and create a new period
        const groupEnd = this.convertTimestamp(records[i - 1].timestampUpdated);
        
        groupedPeriods.push({
          typeOfWatch: watchType,
          rate: currentRate,
          timeRange: {
            start: currentGroupStart,
            end: groupEnd,
            duration: this.calculateDuration(currentGroupStart, groupEnd),
          },
          recordCount,
          isCurrent: false, // Previous periods are not current
        });

        // Initialize new group with the changed frequency
        currentRate = recordRate;
        currentGroupStart = this.convertTimestamp(record.timestampUpdated);
        recordCount = 1;
      } else {
        // Same frequency - just increment the record count
        recordCount++;
      }
    }

    // Add the final group - this represents the current active monitoring period
    const finalEnd = this.convertTimestamp(records[records.length - 1].timestampUpdated);
    groupedPeriods.push({
      typeOfWatch: watchType,
      rate: currentRate,
      timeRange: {
        start: currentGroupStart,
        end: finalEnd,
        duration: this.calculateDuration(currentGroupStart, finalEnd),
      },
      recordCount,
      isCurrent: true, // The last period is always the current active state
    });
  }
}
// ============================================================================
// TABLE COLUMN DEFINITIONS
// ============================================================================

/**
 * Column definitions for the watch history table
 */
const useWatchHistoryColumns = (): IColumn<any>[] => [
  {
    title: "Tegund vöktunar",
    renderCell: (dataItem) => dataItem.tegundVoktunar,
    textAlign: "left",
  },
  {
    title: "Tíðni",
    renderCell: (dataItem) => dataItem.tidni,
    textAlign: "left",
  },
  {
    title: "Tímabil",
    renderCell: (dataItem) => (
      <span>
        {dataItem.isCurrent ? (
          <>
            {dataItem.timabil.split(" - ")[0]} - <em>í dag</em>
          </>
        ) : (
          dataItem.timabil
        )}
      </span>
    ),
    textAlign: "left",
  },
];

/**
 * Column definitions for the status table
 */
const useStatusColumns = (): IColumn<any>[] => [
  {
    title: "Vöktun",
    renderCell: (dataItem) => dataItem.voktun,
    textAlign: "left",
  },
  {
    title: "Síðasta athugun",
    renderCell: (dataItem) => dataItem.sidastaAthugun,
    textAlign: "left",
  },
  {
    title: "Staða",
    renderCell: (dataItem) => dataItem.stada,
    textAlign: "left",
  },
];

/**
 * Column definitions for the changes table
 */
const useChangesColumns = (): IColumn<any>[] => [
  {
    title: "Dagsetning",
    renderCell: (dataItem) => dataItem.date,
    textAlign: "left",
  },
  {
    title: "Breyting",
    renderCell: (dataItem) => dataItem.comment,
    textAlign: "left",
  },
];

/**
 * Column definitions for the PEP connection table
 */
const usePepConnectionColumns = (): IColumn<IPepArray>[] => [
  {
    title: "Aðili",
    renderCell: ({ name }) => name,
    textAlign: "left",
  },
  {
    title: "Tengsl",
    renderCell: ({ connection }) => connection,
    textAlign: "left",
  },
  {
    title: "Staða",
    renderCell: ({ status }) => status,
    textAlign: "left",
  },
  {
    title: "Heimild",
    renderCell: ({ source }) => source,
    textAlign: "left",
  },
];

/**
 * Column definitions for the PEP history table (includes end date)
 */
const usePepHistoryColumns = (): IColumn<IPepArray>[] => {
  const baseColumns = usePepConnectionColumns();
  return [
    {
      title: <span className="view-pep-watch-report__end-date-column">Enda dagsetning</span>,
      renderCell: ({ endDate }) => (endDate ? getTableDate(new Date(endDate), "DD/MM/YYYY", ".") : "-"),
      textAlign: "left",
    },
    ...baseColumns,
  ];
};

// ============================================================================
// CUSTOM HOOKS FOR DATA PROCESSING
// ============================================================================

/**
 * Hook to process and format person details from various data sources
 */
const usePersonDetails = (
  historyData: PepWatchListData[] | Error | null,
  currentData: PepWatchListData | Error | null,
  id: string,
  isHistoryLoading: boolean,
  isCurrentLoading: boolean
) => {
  return useMemo(() => {
    const isHistoryError = historyData instanceof Error;
    const isCurrentError = currentData instanceof Error;
    const isAnyLoading = isHistoryLoading || isCurrentLoading;

    // Show loading state while any data is being fetched
    if (isAnyLoading) {
      return {
        Kennitala: <Loading width="90px" count={1} />,
        Fæðingardagur: <Loading width="115px" count={1} />,
        Land: <Loading width="100px" count={1} />,
        Nafn: <Loading width="130px" count={1} />,
        Tilvísun: <Loading width="100px" count={1} />,
      };
    }

    // Use current data if available, otherwise fallback to history data
    let dataSource: PepWatchListData | null = null;
    if (currentData && !isCurrentError) {
      dataSource = currentData;
    } else if (historyData && !isHistoryError && historyData.length > 0) {
      // Get the most recent record from history
      dataSource = historyData.reduce((latest, current) => {
        const latestTime = latest.timestampUpdated.time;
        const currentTime = current.timestampUpdated.time;
        return currentTime > latestTime ? current : latest;
      });
    }

    if (!dataSource) return null;

    // Format the person's name using available name parts
    const nameParts = {
      firstName: dataSource.personFirstName,
      middleName: dataSource.personMiddleName,
      lastName: dataSource.personLastName,
    };

    // Find country name from country code
    const countryName = dataSource.personCountryCode
      ? //@ts-ignore
        CountryList.find((x) => x.value === dataSource.personCountryCode)?.label ?? "Óþekkt land"
      : "Óþekkt land";

    return {
      Kennitala: isSSN(id) ? insertDash(id ?? "") : "Ógild kennitala",
      Fæðingardagur: dataSource.personDateOfBirth,
      Land: countryName,
      Nafn: formatFullName(nameParts),
      Tilvísun: dataSource.reference ?? "",
    };
  }, [historyData, currentData, id, isHistoryLoading, isCurrentLoading]);
};

/**
 * Hook to process and format watch history table data
 */
const useWatchHistoryTableData = (groupedWatchHistory: GroupedWatchPeriod[]) => {
  return useMemo(() => {
    // Sort by typeOfWatch and then by start date (newest first within each category)
    const sortedHistory = [...groupedWatchHistory].sort((a, b) => {
      // First sort by type of watch (PEP first, then SANCTIONS)
      if (a.typeOfWatch !== b.typeOfWatch) {
        return a.typeOfWatch === "PEP" ? -1 : 1;
      }
      // Within same type, sort by start date (newest first)
      return b.timeRange.start.getTime() - a.timeRange.start.getTime();
    });

    // Filter out "never" entries that represent categories where monitoring never started
    // This removes:
    // 1. The first "never" entry for each category (initial state before monitoring began)
    // 2. Single "never" entries that are the only row for a category
    const filteredHistory = sortedHistory.filter((period, index) => {
      // Skip filtering if rate is not "never"
      if (period.rate !== "never") {
        return true;
      }

      // Get all periods for the same watch type, sorted chronologically (oldest first)
      const sameTypePeriods = sortedHistory
        .filter(p => p.typeOfWatch === period.typeOfWatch)
        .sort((a, b) => a.timeRange.start.getTime() - b.timeRange.start.getTime());

      // If this is the only period for this category and it's "never", hide it
      if (sameTypePeriods.length === 1) {
        return false;
      }

      // Find the chronologically first period for this watch type
      const firstPeriodForType = sameTypePeriods[0];

      // Hide this "never" period only if it's the chronologically first period for its type
      // This represents the initial "never" state before any monitoring began
      const isFirstPeriodForType = period === firstPeriodForType;

      return !isFirstPeriodForType;
    });

    // Transform the filtered data into the format expected by the table component
    return filteredHistory.map((period, index) => ({
      id: index, // Sequential ID for table rows
      tegundVoktunar: translateWatchType(period.typeOfWatch), // Watch type (PEP/Sanctions)
      tidni: translateFrequency(period.rate), // Frequency (daily/quarterly/never)
      timabil: `${getTableDate(period.timeRange.start, "DD/MM/YYYY", ".")} - ${getTableDate(
        period.timeRange.end,
        "DD/MM/YYYY",
        "."
      )}`, // Time period formatted as DD/MM/YYYY - DD/MM/YYYY
      isCurrent: period.isCurrent, // Whether this is the current active period
    }));
  }, [groupedWatchHistory]);
};

/**
 * Hook to process and format status table data
 */
const useStatusTableData = (
  historyData: PepWatchListData[] | Error | null,
  currentData: PepWatchListData | Error | null
) => {
  return useMemo(() => {
    // Prioritize current data, fallback to history data
    let dataSource = null;
    if (currentData && !(currentData instanceof Error)) {
      dataSource = currentData;
    } else if (historyData && !(historyData instanceof Error) && historyData.length > 0) {
      // Get the most recent record for current status
      dataSource = historyData.reduce((latest, current) => {
        const latestTime = latest.timestampUpdated.time;
        const currentTime = current.timestampUpdated.time;
        return currentTime > latestTime ? current : latest;
      });
    }

    if (!dataSource) return [];

    return [
      {
        id: 1,
        voktun: "Stjórnmálaleg tengsl (PEP)",
        sidastaAthugun: dataSource.lastPepCheckTimestamp
          ? getTableDate(new Date(dataSource.lastPepCheckTimestamp.time), "DD/MM/YYYY", ".")
          : "Aldrei",
        stada: !dataSource.lastPepCheckTimestamp ? (
          "Ekki í vöktun"
        ) : (
          <Label labelType={dataSource.pepStatus ? "yes" : "no"} size="lg" text={dataSource.pepStatus ? "Já" : "Nei"} />
        ),
      },
      {
        id: 2,
        voktun: "Alþjóðlegir válistar",
        sidastaAthugun: dataSource.lastSanctionsCheckTimestamp
          ? getTableDate(new Date(dataSource.lastSanctionsCheckTimestamp.time), "DD/MM/YYYY", ".")
          : "Aldrei",
        stada: !dataSource.lastSanctionsCheckTimestamp ? (
          "Ekki í vöktun"
        ) : (
          <Label
            labelType={dataSource.sanctionsStatus ? "yes" : "no"}
            size="lg"
            text={dataSource.sanctionsStatus ? "Já" : "Nei"}
          />
        ),
      },
    ];
  }, [historyData, currentData]);
};

/**
 * Hook to process and format changes table data
 */
const useChangesTableData = (historyData: PepWatchListData[] | Error | null) => {
  return useMemo(() => {
    if (!historyData || historyData instanceof Error || historyData.length === 0) return [];

    // Sort records by timestamp to ensure chronological order
    const sortedRecords = [...historyData].sort((a, b) => a.timestampUpdated.time - b.timestampUpdated.time);

    const changes = [];
    let previousComment = "";

    for (const record of sortedRecords) {
      const currentComment = record.lastChangeComment || "";

      // Only add if comment changed and there's actually a timestamp and comment
      if (currentComment !== previousComment && record.lastChangeTimestamp && currentComment.trim() !== "") {
        changes.push({
          id: changes.length + 1,
          date: getTableDate(new Date(record.lastChangeTimestamp.time), "DD/MM/YYYY", "."),
          comment: currentComment,
        });
        previousComment = currentComment;
      }
    }

    // Return in reverse order (newest first)
    return changes.reverse();
  }, [historyData]);
};

/**
 * Hook to process and format sanctions table data
 */
const useSanctionsTableData = (currentData: PepWatchListData | Error | null) => {
  return useMemo(() => {
    // Prioritize current data, fallback to history data
    let dataSource = null;
    if (currentData && !(currentData instanceof Error)) {
      dataSource = [currentData]; // Wrap in array to match history data structure
    } else {
      return [];
    }

    if (!dataSource || dataSource.length === 0) return [];

    const lastSanctionsResponse = dataSource[dataSource.length - 1].lastSanctionsResponse;
    if (!lastSanctionsResponse || lastSanctionsResponse.trim() === "") {
      return [];
    }
    const parsedResponse = JSON.parse(lastSanctionsResponse);
    return Array.isArray(parsedResponse.match) ? parsedResponse.match : [];
  }, [currentData]);
};

// ============================================================================
// COMPONENT SECTIONS
// ============================================================================

/**
 * Component for rendering person information section
 */
const PersonInfoSection: React.FC<{
  personDetails: Record<string, React.ReactNode> | null;
}> = ({ personDetails }) => {
  if (!personDetails) return null;

  return (
    <div className="info-col-wrapper">
      {Object.entries(personDetails).map(([key, value]) => (
        <div key={key} className="info-col">
          <div className="info-item">
            <span className="title">{key}</span>
            <span className="value">{value}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Component for rendering report header with download link
 */
const ReportHeaderSection: React.FC<{ id: string }> = ({ id }) => (
  <div className="report-aside-component">
    <div className="link-wrapper">
      <Link targetBlank linkSize="15" url={`/Pdf/PepWatchReport/${id}`} icon="pdf">
        Sækja skýrslu
      </Link>
    </div>
    <div className="italic-text">Skýrsla sótt: {getTableDate(new Date(), "DD/MM/YYYY HH:MM:SS", ".")}</div>
  </div>
);

/**
 * Component for rendering individual content sections with error handling
 */
const ContentSection: React.FC<{
  title: string;
  isLoading: boolean;
  error: Error | null;
  children: React.ReactNode;
}> = ({ title, isLoading, error, children }) => {
  if (isLoading) {
    return (
      <div className="view-pep-watch-report__section">
        <h4>{title}</h4>
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="view-pep-watch-report__section">
        <h4>{title}</h4>
        <Alert type="error" headText={error.message} />
      </div>
    );
  }

  return (
    <div className="view-pep-watch-report__section">
      <h4>{title}</h4>
      {children}
    </div>
  );
};

/**
 * Component for rendering PEP history snapshots
 */
const PepHistorySnapshotsSection: React.FC<{
  pepHistoryData: ParsedPepHistory[] | null;
}> = ({ pepHistoryData }) => {
  const pepHistoryColumns = usePepHistoryColumns();

  if (pepHistoryData === null || pepHistoryData.length === 0) {
    return <Table tableSize="lg" data={[]} columns={pepHistoryColumns} showEmptyTableText />;
  }

  return (
    <>
      {pepHistoryData.map((history) => {
        // Sort the entries for this snapshot
        const sortedEntries = sortHistorySnapshotEntries(history.snapshotEntries);

        return (
          <div className="view-pep-watch-report__pep-history-snapshot" key={history.snapshotTimestamp.time}>
            <span className="date">
              Afrit tekið: {getTableDate(new Date(history.snapshotTimestamp.time), "DD/MM/YYYY", ".")}
            </span>
            <Table tableSize="lg" data={sortedEntries} columns={pepHistoryColumns} showEmptyTableText />
          </div>
        );
      })}
    </>
  );
};

// ============================================================================
// CUSTOM HOOKS FOR API CALLS
// ============================================================================

/**
 * Hook for fetching watch history data
 */
const useWatchHistoryData = (id: string) => {
  const [historyData, setHistoryData] = useState<PepWatchListData[] | Error | null>(null);
  const [sanctionsHistoryData, setSanctionsHistoryData] = useState<PepResult[] | null>(null);
  const [isHistoryLoading, setIsHistoryLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchHistoryData = async () => {
      setIsHistoryLoading(true);
      try {
        const response = await fetch(`/pep/GetWatchHistory?ssn=${id}`);
        if (!response.ok) {
          throw new Error(`Error fetching history: ${response.status}`);
        }
        const responseBody: IKeldanApiResponse<PepWatchResponse<PepWatchListData[]>> = await response.json();
        if (responseBody.success) {
          if (responseBody.result?.data) {
            setHistoryData(responseBody.result.data);

            // Parse the sanctions responses
            const parsedSanctions = parseSanctionsResponses(responseBody.result.data);
            setSanctionsHistoryData(parsedSanctions);
          }
        } else {
          throw new Error("Ekki tókst að ná í vöktunarsögu");
        }
      } catch (err) {
        console.error("Error fetching PEP watch history:", err);
        setHistoryData(err instanceof Error ? err : new Error("Unknown error fetching history"));
      } finally {
        setIsHistoryLoading(false);
      }
    };

    if (id) {
      fetchHistoryData();
    }
  }, [id]);

  return { historyData, sanctionsHistoryData, isHistoryLoading };
};

/**
 * Hook for fetching current watch data
 */
const useCurrentWatchData = (id: string) => {
  const [currentData, setCurrentData] = useState<PepWatchListData | Error | null>(null);
  const [isCurrentLoading, setIsCurrentLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchCurrentData = async () => {
      setIsCurrentLoading(true);
      try {
        const response = await fetch(`/pep/GetPersonDataWatchList?id=${id}`);
        if (!response.ok) {
          throw new Error(`Error fetching current data: ${response.status}`);
        }
        const responseBody: IKeldanApiResponse<PepWatchResponse<PepWatchListData>> = await response.json();
        if (responseBody.success) {
          if (responseBody.result?.data) {
            setCurrentData(responseBody.result.data);
          }
        } else {
          throw new Error("Current data API returned unsuccessful response");
        }
      } catch (err) {
        console.error("Error fetching current PEP watch data:", err);
        setCurrentData(err instanceof Error ? err : new Error("Unknown error fetching current data"));
      } finally {
        setIsCurrentLoading(false);
      }
    };

    if (id) {
      fetchCurrentData();
    }
  }, [id]);

  return { currentData, isCurrentLoading };
};

/**
 * Hook for fetching current PEP connection data
 */
const useCurrentPepConnection = (id: string, currentData: PepWatchListData | Error | null) => {
  const [pepConnectionData, setPepConnectionData] = useState<IPepData | null>(null);
  const [isConnectionLoading, setIsConnectionLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchCurrentPepConnection = async () => {
      setIsConnectionLoading(true);
      try {
        const response = await fetch(`/pep/GetPersonPepResponse?ssn=${id}`);
        if (!response.ok) {
          throw new Error(`Error fetching current pep connections: ${response.status}`);
        }
        const responseBody: IKeldanApiResponse<PepWatchResponse<IPepType>> = await response.json();
        if (
          responseBody.success &&
          responseBody.result &&
          responseBody.result.data.results &&
          responseBody.result.data.results[0]
        ) {
          // Sort the entries and set the data
          setPepConnectionData({
            name: responseBody.result.data.results[0].name,
            ssn: responseBody.result.data.results[0].ssn,
            connected: responseBody.result.data.results[0].connected,
            entries: sortEntries(responseBody.result.data.results ?? null),
          });
        }
      } catch (err) {
        console.error("Error fetching PEP watch history:", err);
      } finally {
        setIsConnectionLoading(false);
      }
    };

    // Only fetch if id is valid SSN and currentData is not an error && pepstatus is true
    if (id && isSSN(id) && !(currentData instanceof Error) && currentData?.pepStatus) {
      fetchCurrentPepConnection();
    }
  }, [id, currentData]);

  return { pepConnectionData, isConnectionLoading };
};

/**
 * Hook for fetching PEP history data
 */
const usePepHistoryData = (id: string) => {
  const [pepHistoryData, setPepHistoryData] = useState<ParsedPepHistory[] | null>(null);
  const [isPepHistoryLoading, setIsPepHistoryLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchPepHistory = async () => {
      setIsPepHistoryLoading(true);
      try {
        const response = await fetch(`/pep/GetPepWatchHistory?id=${id}`);
        if (!response.ok) {
          throw new Error(`Error fetching PEP history: ${response.status}`);
        }
        const responseBody: IKeldanApiResponse<PepWatchResponse<PepHistorySnapshot[]>> = await response.json();
        if (responseBody.success && responseBody.result) {
          // Parse into the simple format you want
          const parsedHistory: ParsedPepHistory[] = parsePepHistoryResponse(responseBody.result.data);
          setPepHistoryData(parsedHistory);
        } else {
          throw new Error("PEP history API returned unsuccessful response");
        }
      } catch (err) {
        console.error("Error fetching PEP history data:", err);
        setPepHistoryData(null);
      } finally {
        setIsPepHistoryLoading(false);
      }
    };

    if (id) {
      fetchPepHistory();
    }
  }, [id]);

  return { pepHistoryData, isPepHistoryLoading };
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Main component for viewing PEP watch reports
 * Displays comprehensive information about a person's PEP and sanctions watch status
 */
const ViewPepWatchReportPage: React.FC<{ id: string }> = ({ id }) => {
  // Initialize the watch history grouper
  const [grouper] = useState(new WatchHistoryGrouper());

  const [SANCTIONS_connectionModal_isOpen, setSANCTIONS_ConnectionModal_isOpen] = useState(false);
  const [SANCTIONS_connectionModal_data, setSANCTIONS_ConnectionModal_data] = useState<PepResult | undefined>(undefined);

  // Fetch all required data using custom hooks
  const { historyData, sanctionsHistoryData, isHistoryLoading } = useWatchHistoryData(id);
  const { currentData, isCurrentLoading } = useCurrentWatchData(id);
  const { pepConnectionData } = useCurrentPepConnection(id, currentData);
  const { pepHistoryData } = usePepHistoryData(id);

  // Process data using custom hooks
  const personDetails = usePersonDetails(historyData, currentData, id, isHistoryLoading, isCurrentLoading);

  // Memoized grouped watch history
  const groupedWatchHistory = useMemo(() => {
    // Return empty array if no history data is available
    if (!historyData || historyData instanceof Error || historyData.length === 0) {
      return [];
    }
    // Pass both history data and current data to the grouper
    return grouper.groupWatchHistory(historyData, currentData);
  }, [historyData, currentData, grouper]);

  // Process table data using custom hooks
  const watchHistoryTableData = useWatchHistoryTableData(groupedWatchHistory);
  const statusTableData = useStatusTableData(historyData, currentData);
  const changesTableData = useChangesTableData(historyData);
  const sanctionsTableData = useSanctionsTableData(currentData);

  // Get column definitions
  const watchHistoryColumns = useWatchHistoryColumns();
  const statusColumns = useStatusColumns();
  const changesColumns = useChangesColumns();
  const sanctionsColumns: IColumn<any>[] = [
  {
    title: "Match rating",
    renderCell: ({ match_rate }) => match_rate,
    textAlign: "left",
  },
  {
    title: "Samsvörun",
    renderCell: ({ category }) => category,
    textAlign: "left",
  },
  {
    title: "Lýsing",
    renderCell: ({ categories }) => formatCategories(categories),
    textAlign: "left",
  },
  {
    title: "Nánar",
    renderCell: (item) => (
      <Button
        buttonType="secondary"
        size="sm"
        onClick={() => {
          setSANCTIONS_ConnectionModal_isOpen(true);
          setSANCTIONS_ConnectionModal_data(item);
        }}
      >
        Skoða
      </Button>
    )
  }
]
  const pepConnectionColumns = usePepConnectionColumns();
  const sanctionsHistoryColumns: IColumn<any>[] = [
    {
      title: <span className="view-pep-watch-report__end-date-column">Enda dagsetning</span>,
      renderCell: ({ endDate }) => (endDate ? getTableDate(new Date(endDate), "DD/MM/YYYY", ".") : "-"),
      textAlign: "left",
    },
    {
      title: "Match rating",
      renderCell: ({ match_rate }) => match_rate,
      textAlign: "left",
    },
    {
      title: "Samsvörun",
      renderCell: ({ category }) => category,
      textAlign: "left",
    },
    {
      title: "Lýsing",
      renderCell: ({ categories }) => formatCategories(categories),
      textAlign: "left",
    },
    {
      title: "Nánar",
      renderCell: (item) => (
        <Button
          buttonType="secondary"
          size="sm"
          onClick={() => {
            setSANCTIONS_ConnectionModal_isOpen(true);
            setSANCTIONS_ConnectionModal_data(item);
          }}
        >
          Skoða
        </Button>
      )
    }
  ]

  // Helper function to determine if PEP data should be shown
  const shouldShowPepData = () => {
    return (
      (currentData && !(currentData instanceof Error) && currentData.pepStatus) ||
      (historyData && !(historyData instanceof Error) && historyData[historyData.length - 1]?.pepStatus)
    );
  };

  // Helper function to determine if sanctions data should be shown
  const shouldShowSanctionsData = () => {
    return (
      (currentData && !(currentData instanceof Error) && currentData.sanctionsStatus) ||
      (historyData && !(historyData instanceof Error) && historyData[historyData?.length - 1]?.sanctionsStatus)
    );
  };

  return (
    <div className="main KCL_viewPep KCL_viewPepWatchReport KCL_individual-profile assets-report">
      <div className="shell">
        <HeadSection
          breadcrumbs={
              <Link href="/Valistar/Leit#Pep-vaktin" icon='back' linkSize='14'>
                  PEP vaktin - yfirlit
              </Link>
          }
          title={"PEP vaktin - skýrsla"}
        />
        
        <PersonInfoSection personDetails={personDetails} />

        <DisplayBox
          title="PEP vaktin - niðurstöður"
          asideComponent={<ReportHeaderSection id={id} />}
        >
          {/* Watch History Section */}
          <ContentSection
            title="Vöktunarsaga"
            isLoading={isHistoryLoading}
            error={historyData instanceof Error ? historyData : null}
          >
            <Table tableSize="lg" data={watchHistoryTableData} columns={watchHistoryColumns} showEmptyTableText />
          </ContentSection>

          <div className="view-pep-watch-report__results-section">
            {/* Status Section - Uses both current and history data */}
            <div>
              <h3>Niðurstöður</h3>
              {isCurrentLoading || isHistoryLoading ? (
                <Loading />
              ) : currentData instanceof Error && historyData instanceof Error ? (
                <Alert type="error" headText="Unable to load status data" />
              ) : (
                <Table tableSize="lg" data={statusTableData} columns={statusColumns} />
              )}
            </div>

            {/* PEP Connections Section */}
            {shouldShowPepData() && (
              <div>
                <h6>Stjórnmálaleg tengsl (PEP)</h6>
                <Table tableSize="lg" data={pepConnectionData?.entries ?? []} columns={pepConnectionColumns} />
              </div>
            )}

            {/* Sanctions Section */}
            {shouldShowSanctionsData() && (
              <div>
                <h6>Alþjóðlegir válistar</h6>
                <Table tableSize="lg" data={sanctionsTableData} columns={sanctionsColumns} />
              </div>
            )}

            {/* Changes Section - Only from history data */}
            <ContentSection
              title=""
              isLoading={isHistoryLoading}
              error={historyData instanceof Error ? historyData : null}
            >
              <h6>Breytingarsaga</h6>
              <Table tableSize="lg" data={changesTableData} columns={changesColumns} showEmptyTableText />
            </ContentSection>
          </div>

          <div className="view-pep-watch-report__historical-results">
            <h4 className="view-pep-watch-report__historical-title">Eldri niðurstöður</h4>
            
            <div>
              <h6>Stjórnmálaleg tengsl (PEP)</h6>
              <PepHistorySnapshotsSection pepHistoryData={pepHistoryData} />
            </div>
            
            <div>
              <h6>Alþjóðlegir válistar</h6>
              <Table 
                tableSize="lg" 
                data={sanctionsHistoryData ?? []} 
                columns={sanctionsHistoryColumns} 
                showEmptyTableText 
              />
            </div>
          </div>
        </DisplayBox>
      </div>
      
      <ForeignPepModal
        show={SANCTIONS_connectionModal_isOpen}
        close={() => setSANCTIONS_ConnectionModal_isOpen(false)}
        data={SANCTIONS_connectionModal_data ? [SANCTIONS_connectionModal_data] : undefined}
      />
    </div>
  );
};

export default ViewPepWatchReportPage;